import { SendbirdChatWith } from '@sendbird/chat';
import {
  GroupChannel,
  GroupChannelListOrder,
  GroupChannelListQuery,
  GroupChannelModule,
} from '@sendbird/chat/groupChannel';
import { FC } from 'react';
import { Helmet } from 'react-helmet';
import { LoaderFunction, redirect, useLoaderData } from 'react-router';

import { renderTemplateId } from '@/actions/messageTemplate';
import { MiddlewareContext, mapQueryParams } from '@/routes/routesMiddleware';
import { getCache, setCache } from '@/utils';

import Messenger from '../components/Messenger';
import { channelIdToUrl, channelUrlToId } from '../sendbird';
import { LEGACY_CHAT_CUSTOM_TYPES, isLegacyChatChannel } from '../utils';

async function fetchLastActiveChannel(
  sb: SendbirdChatWith<GroupChannelModule[]>
): Promise<GroupChannel | null> {
  const query: GroupChannelListQuery = sb.groupChannel.createMyGroupChannelListQuery({
    order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
    limit: 1,
    includeEmpty: false,
    customTypesFilter: LEGACY_CHAT_CUSTOM_TYPES,
  });

  if (query.hasNext) {
    const channels = await query.next();
    return channels[0];
  }

  return null;
}

interface MessagingLoaderData {
  channelUrl?: string;
  text?: string;
}

export function messagingMiddleware({ store, sendbird }: MiddlewareContext): LoaderFunction {
  return async ({ params, request }): Promise<Response | MessagingLoaderData> => {
    const query = mapQueryParams(request.url);
    const { viewer } = store.getState();
    const { channelId } = params;
    const channelUrl = channelId && channelIdToUrl(channelId);

    const requestId = query.get('request');
    const expertId = query.get('expert');
    const template = query.get('template');

    if (!viewer.id) {
      throw new Error('Not logged in');
    }

    const session = getCache('session') || {};

    if (channelUrl) {
      // Deep link to a specific channel. Verify it's a legacy chat channel before opening it
      // in the legacy Messenger — engagement channels (custom_type='engagement') have their
      // own surface. Without this a /messaging/:id link (or a lastChannelUrl poisoned by an
      // earlier engagement deep link) would render an engagement thread in the legacy shell,
      // which the list-query filter alone does not cover (ENG-5503).
      const sb = await sendbird.getSession();
      const channel = sb && (await sb.groupChannel.getChannel(channelUrl).catch(() => null));
      if (channel && !isLegacyChatChannel(channel)) {
        if (session.lastChannelUrl === channelUrl) {
          session.lastChannelUrl = undefined;
          setCache('session', session);
        }
        return redirect('/messaging');
      }
    } else {
      let lastChannelUrl = session.lastChannelUrl;
      if (!lastChannelUrl) {
        const sb = await sendbird.getSession();
        if (sb) {
          const lastActiveChannel = await fetchLastActiveChannel(sb);
          lastChannelUrl = lastActiveChannel?.url;
        }
      }
      if (lastChannelUrl) return redirect(`/messaging/${channelUrlToId(lastChannelUrl)}`);
    }

    session.lastChannelUrl = channelUrl;
    setCache('session', session);

    let text;
    if (template && requestId && expertId) {
      const t = await store.dispatch(
        renderTemplateId(template, {
          senderId: viewer.id,
          requestId: requestId,
          expertId: expertId,
        })
      );
      text = t.rendered;
    }

    return {
      channelUrl,
      text,
    };
  };
}

const MessagingPage: FC = () => {
  const { channelUrl, text } = useLoaderData() as MessagingLoaderData;
  return (
    <>
      <Helmet>
        <title>Messages</title>
      </Helmet>
      <Messenger channelUrl={channelUrl} text={text} />
    </>
  );
};

export default MessagingPage;
